import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { Clock } from 'lucide-react';
import api from '../api/axios';
import SearchableSelect from '../components/SearchableSelect';
import Table from '../components/Table';
import 'react-datepicker/dist/react-datepicker.css';
import * as moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { formatMinutes } from '../utils';
import { useAlert } from '../components/AlertContext';

import {
  ArrowDownTrayIcon,
  FunnelIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';

function ShiftSelectCell({ event, shifts, onUpdate, t }) {
  const [updating, setUpdating] = useState(false);
  const { showAlert } = useAlert();
  const finalAvailableShifts = shifts?.filter(s => s.roleId === event.role?._id) || [];

  const handleShiftChange = async (e) => {
    const newShiftId = e.target.value;
    if (!newShiftId) return;

    setUpdating(true);
    try {
      await api.put(`/events/${event._id}/changeShift`, { shiftId: newShiftId });
      if (onUpdate) onUpdate();
    } catch (err) {
      showAlert(err.response?.data?.message || "Failed to change shift", 'error');
    } finally {
      setUpdating(false);
    }
  };

  if (updating) {
    return (
      <div className="flex justify-end pr-8">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <select
      value={event.shift?._id || ''}
      onChange={handleShiftChange}
      className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-sm w-48"
    >
      <option value="" disabled>{t('selectShift') || 'Select'}</option>
      {finalAvailableShifts.map(s => (
        <option key={s._id} value={s._id}>{s.name} ({s.startDateString} - {s.endDateString})</option>
      ))}
    </select>
  );
}

export default function CompanyEventLogsPage() {
  const today = new Date();
  const defaultEnd = today;
  const defaultStart = new Date(today);
  defaultStart.setDate(today.getDate() - 1);
  defaultStart.setHours(0, 0, 0, 0);

  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();

  const [dateRange, setDateRange] = useState([defaultStart, defaultEnd]);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [queryEventId, setQueryEventId] = useState(new URLSearchParams(window.location.search).get('eventId'));

  const clearQueryEventId = () => {
    if (queryEventId) {
      setQueryEventId(null);
      const searchParams = new URL(window.location);
      searchParams.searchParams.delete('eventId');
      window.history.replaceState({}, '', searchParams);
    }
  };

  // New states for users dropdown
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(''); // empty = all users
  const [selectedRoleId, setSelectedRoleId] = useState(''); // empty = all roles

  // Refresh Shifts states
  const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);
  const [refreshStartDate, setRefreshStartDate] = useState(defaultStart);
  const [refreshEndDate, setRefreshEndDate] = useState(defaultEnd);

  const maxRangeDays = 90;

  const handleExportExcel = async () => {
    if (!startDate || !endDate || !companyId) return;

    try {
      const lang = localStorage.getItem('language') || 'en';

      const start = moment.tz(startDate, 'Asia/Tashkent').utc().toISOString();
      const end = moment.tz(endDate, 'Asia/Tashkent').utc().toISOString();

      let url = `/events/${companyId}/log/export?startDate=${start}&endDate=${end}&lang=${lang}`;
      if (selectedUserId) {
        url += `&userId=${encodeURIComponent(selectedUserId)}`;
      }

      // Fetch the Excel file as a blob
      const response = await api.get(url, { responseType: 'blob' });

      // Create a link to download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `events_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export Excel', err);
      showAlert(t('exportFailed'), 'error');
    }
  };

  const handleRefreshConfirm = async () => {
    if (!window.confirm(t('confirmRefreshShifts'))) return;

    try {
      setLoading(true);
      const start = moment.tz(refreshStartDate, 'Asia/Tashkent').utc().toISOString();
      const end = moment.tz(refreshEndDate, 'Asia/Tashkent').utc().toISOString();

      const body = {
        start,
        end
      }
      await api.post(`/companies/${companyId}/refreshShifts`, body);

      setIsRefreshModalOpen(false);
      fetchEvents();
      showAlert(t('shiftsRefreshedSuccessfully'), 'success');
    } catch (err) {
      console.error('Failed to refresh shifts', err);
      showAlert(err.response?.data?.message || t('failedToRefreshShifts'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch company ID + users list
  useEffect(() => {
    const fetchData = async () => {
      try {
        const cid = localStorage.getItem("companyId");
        if (!cid) throw new Error('Company ID not found');

        setCompanyId(cid);

        const usersRes = await api.get(`/companies/${cid}/users`);
        const userList = usersRes.data?.data || [];
        setUsers(userList);

        const rolesRes = await api.get(`/companies/${cid}/roles`);
        setRoles(rolesRes.data?.data || []);

        const shiftsRes = await api.get(`/companies/${cid}/shifts`);
        setShifts(shiftsRes.data?.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load company/users data');
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (dateRange[0]) setStartDate(dateRange[0]);
    if (dateRange[1]) setEndDate(dateRange[1]);
  }, [dateRange]);

  const fetchEvents = async (ignoreEventId = false) => {
    if (!companyId || !startDate || !endDate) return;

    if (startDate > endDate) {
      setError('Start datetime cannot be after end datetime');
      return;
    }

    const diffDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays > maxRangeDays) {
      setError(`Maximum date range is ${maxRangeDays} days`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const start = moment.tz(startDate, 'Asia/Tashkent').utc().toISOString();
      const end = moment.tz(endDate, 'Asia/Tashkent').utc().toISOString();

      let url = `/events/${companyId}/log?startDate=${start}&endDate=${end}`;
      if (selectedUserId) {
        url += `&userId=${encodeURIComponent(selectedUserId)}`;
      }
      if (selectedRoleId) {
        url += `&roleId=${encodeURIComponent(selectedRoleId)}`;
      }
      if (queryEventId && !ignoreEventId) {
        url += `&eventId=${encodeURIComponent(queryEventId)}`;
      }

      const res = await api.get(url);
      const eventList = res.data?.data || [];

      setEvents(eventList);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load event logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchEvents();
    }
  }, [companyId]);

  const handleApply = () => {
    let ignore = false;
    if (queryEventId) {
      clearQueryEventId();
      ignore = true;
    }
    fetchEvents(ignore);
  };

  return (
    <div className="p-6 text-white min-h-screen bg-gray-900 space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-sky-600">{t('eventLogs')}</h1>

        <div className="flex flex-wrap items-end gap-4 w-full sm:w-auto">
          <div className="w-full sm:w-auto">
            <label className="block text-sm text-gray-400 mb-1">{t('role')}</label>
            <select
              value={selectedRoleId}
              onChange={(e) => {
                const roleId = e.target.value;
                setSelectedRoleId(roleId);
                setSelectedUserId('');
                clearQueryEventId();
              }}
              className="bg-gray-800 border border-gray-700 rounded px-4 py-2.5 text-white w-full sm:w-52"
            >
              <option value="">{t('allRoles')}</option>
              {roles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full sm:w-auto">
            <label className="block text-sm text-gray-400 mb-1">{t('user')}</label>
            <SearchableSelect
              value={selectedUserId}
              onChange={(val) => { setSelectedUserId(val); clearQueryEventId(); }}
              placeholder={t('allUsers')}
              options={users
                .filter(u => !selectedRoleId || (u.role && (u.role._id === selectedRoleId || u.role === selectedRoleId)))
                .map(u => ({ value: u.systemId, label: `${u.systemId} - ${u.firstName || ''} ${u.lastName || ''}`.trim() }))
              }
            />
          </div>

          <div style={{ zIndex: 999 }} className="w-full sm:w-auto">
            <label className="block text-sm text-gray-400 mb-1">{t('dateRange')}</label>
            <DatePicker
              selectsRange={true}
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={(update) => { setDateRange(update); clearQueryEventId(); }}
              minDate={new Date(new Date().getTime() - maxRangeDays * 24 * 60 * 60 * 1000)}
              maxDate={new Date()}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="yyyy-MM-dd HH:mm"
              className="bg-gray-800 border border-gray-700 rounded px-4 py-2.5 w-full sm:w-80 text-white cursor-pointer"
              wrapperClassName="w-full"
              placeholderText="Select date & time range"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={handleApply}
              disabled={loading || !companyId}
              className={`flex justify-center items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all duration-200 w-full sm:w-auto shadow-lg ${loading || !companyId
                ? 'bg-gray-700 cursor-not-allowed text-gray-400'
                : 'bg-sky-600 hover:bg-sky-500 text-white shadow-sky-900/20 active:scale-95'
                }`}
            >
              <FunnelIcon className="w-5 h-5" />
              <span>{t('apply')}</span>
            </button>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={handleExportExcel}
              disabled={loading || !companyId}
              className={`flex justify-center items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 w-full sm:w-auto shadow-lg ${loading || !companyId
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-sky-600 text-white hover:bg-sky-500 shadow-sky-900/20 active:scale-95'
                }`}
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              <span>{t('exportToExcel')}</span>
            </button>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => setIsRefreshModalOpen(true)}
              disabled={loading || !companyId}
              className={`flex justify-center items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 w-full sm:w-auto shadow-lg ${loading || !companyId
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-sky-600 text-white hover:bg-sky-500 shadow-sky-900/20 active:scale-95'
                }`}
            >
              <ArrowsRightLeftIcon className="w-5 h-5" />
              <span>{t('refreshShifts')}</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/60 border border-red-700 text-red-100 px-5 py-3 rounded-xl">
          {error}
        </div>
      )}

      <div className="bg-gray-800 rounded-2xl shadow overflow-hidden">
        <div className="p-5 border-b border-gray-700">
          <h2 className="text-xl font-semibold">
            {selectedUserId ? `(${selectedUserId})` : ''}   {events.length} {t('records')}
          </h2>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-400">{t('loadingEvents')}</div>
        ) : events.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {t('noEventsFound')}
          </div>
        ) : (
          <Table
            data={events}
            columns={[
              { header: t('user'), accessor: 'userName', sortAccessor: (row) => row.userName || row.userId, sortable: true, className: "font-medium whitespace-nowrap", render: (event) => event.userName || event.userId },
              {
                header: t('checkIn'),
                sortAccessor: 'checkInLocal',
                align: 'right',
                className: 'whitespace-nowrap',
                render: (event) => (
                  <div className={`relative group inline-block ${event.systemAdjusted ? 'text-yellow-600 cursor-pointer' : ''}`}>
                    {event.checkInLocal}
                    {event.systemAdjusted && (
                      <span className="absolute bottom-full right-0 mb-1 hidden group-hover:block bg-gray-700 text-white text-xs rounded px-2 py-1 z-50 whitespace-nowrap">
                        Automatically closed by system
                      </span>
                    )}
                  </div>
                )
              },
              {
                header: t('checkOut'),
                sortAccessor: 'checkOutLocal',
                align: 'right',
                className: 'whitespace-nowrap',
                render: (event) => (
                  <div className={`relative group inline-block ${event.systemAdjusted ? 'text-yellow-600 cursor-pointer' : ''}`}>
                    {event.checkOutLocal}
                    {event.systemAdjusted && (
                      <span className="absolute bottom-full right-0 mb-1 hidden group-hover:block bg-gray-700 text-white text-xs rounded px-2 py-1 z-50 whitespace-nowrap">
                        Automatically closed by system
                      </span>
                    )}
                  </div>
                )
              },
              {
                header: t('workedHours'),
                sortAccessor: 'minutesWorked',
                align: 'right',
                className: 'font-medium whitespace-nowrap',
                render: (event) => event.minutesWorked ? formatMinutes(event.minutesWorked, i18n.language) : '—'
              },
              { header: t('lateMinutes'), accessor: 'shift.lateMinutes', sortable: true, align: 'right', render: (event) => event.shift?.lateMinutes || 0 },
              {
                header: t('arrival'),
                sortAccessor: (row) => Math.max(row.shift?.lateMinutes || 0, row.shift?.earlyMinutes || 0),
                align: 'right',
                render: (event) => {
                  const shift = event.shift;
                  if (!shift || (shift.lateMinutes === 0 && shift.earlyMinutes === 0)) {
                    return (
                      <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                        <span className="font-medium">{shift?.name || 'N/A'} — {t('onTime')}</span>
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      </div>
                    );
                  }
                  const minutes = shift.lateMinutes > 0 ? shift.lateMinutes : shift.earlyMinutes;
                  const label = shift.lateMinutes > 0 ? t('late') : t('early');
                  const dotColor = shift.lateMinutes > 0 ? 'bg-red-500' : 'bg-green-500';
                  return (
                    <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                      <span className="font-medium">{shift.name || 'N/A'} — {minutes}{t('minutes')} {label}</span>
                      <div className={`w-2 h-2 rounded-full ${dotColor}`}></div>
                    </div>
                  );
                }
              },
              { header: 'Shift', accessor: 'shift.name', align: 'right', render: (event) => <ShiftSelectCell event={event} shifts={shifts} onUpdate={() => fetchEvents()} t={t} /> }
            ]}
            defaultSortColumn={1}
            keyExtractor={(row) => row._id}
            containerClassName="max-h-[70vh] overflow-auto"
            headerClassName="bg-gray-700 sticky top-0 z-10"
          />
        )}
      </div>

      {isRefreshModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl w-full max-w-md p-6 border border-gray-700 shadow-2xl animate-in fade-in zoom-in duration-200 relative">
            {loading && (
              <div className="absolute inset-0 bg-gray-800/80 backdrop-blur-[2px] rounded-2xl flex flex-col items-center justify-center z-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                <span className="text-blue-400 font-semibold text-lg">{t('refreshing')}...</span>
              </div>
            )}
            <h3 className="text-xl font-semibold mb-4 text-blue-400">{t('refreshShifts')}</h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              {t('refreshShiftsDescription')}
            </p>

            <div className="space-y-4 mb-8">
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('startDate')}</label>
                <DatePicker
                  selected={refreshStartDate}
                  onChange={date => setRefreshStartDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="yyyy-MM-dd HH:mm"
                  className="bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 w-full text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  wrapperClassName="w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('endDate')}</label>
                <DatePicker
                  selected={refreshEndDate}
                  onChange={date => setRefreshEndDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="yyyy-MM-dd HH:mm"
                  className="bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 w-full text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  wrapperClassName="w-full"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsRefreshModalOpen(false)}
                disabled={loading}
                className="flex-1 py-3 rounded-xl font-medium bg-gray-700 hover:bg-gray-600 transition disabled:opacity-50"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleRefreshConfirm}
                disabled={loading}
                className="flex-1 py-3 rounded-xl font-medium bg-blue-600 hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                {t('refresh')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}